import React, { useState } from 'react';
import { Search, ThumbsUp, ThumbsDown, ChevronDown, Loader2, Plus, RefreshCw, PauseCircle, X, HelpCircle } from 'lucide-react';
import { trackDashboardReached } from '@/lib/gtm';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import { queriesService, QueryTestResult } from '@/api/queriesService';
import { websiteService } from '@/api/websiteService';
import QueryDetailModal from '@/components/QueryDetailModal';
import ResultsPaywallOverlay from '@/components/ResultsPaywallOverlay';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LLM_PROVIDER_LOGOS, ALL_LLM_PROVIDERS } from '@/types/llm-provider';
import { useAuth } from '@/context/AuthContext';
import { useWebsite } from '@/context/WebsiteContext';

function formatTimeAgo(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

const DashboardQueriesPage: React.FC = () => {
  const { userSession } = useAuth();
  const { selectedWebsite } = useWebsite();
  const queryClient = useQueryClient();
  const isSubscribed = userSession?.subscription?.status === 'active' || userSession?.subscription?.status === 'trialing';
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMentions, setFilterMentions] = useState<'all' | 'mentioned' | 'not-mentioned'>('all');
  const [selectedQuery, setSelectedQuery] = useState<QueryTestResult | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddQueryModal, setShowAddQueryModal] = useState(false);
  const [newQueryText, setNewQueryText] = useState('');

  const { data: websiteDetail } = useQuery({
    queryKey: ['website-detail', selectedWebsite?.id],
    queryFn: () => websiteService.getWebsite(selectedWebsite!.id),
    enabled: !!selectedWebsite?.id && isSubscribed,
    staleTime: 30 * 1000,
  });

  const retestSettingsMutation = useMutation({
    mutationFn: (retestEnabled: boolean) =>
      websiteService.updateRetestSettings(selectedWebsite!.id, { retestEnabled }),
    onSuccess: (_, retestEnabled) => {
      queryClient.invalidateQueries({ queryKey: ['website-detail', selectedWebsite?.id] });
      queryClient.invalidateQueries({ queryKey: ['user-session'] });
      toast.success(retestEnabled ? 'Daily retesting enabled' : 'Daily retesting disabled');
    },
  });

  const toggleQueryRetestMutation = useMutation({
    mutationFn: ({ query, enabled }: { query: string; enabled: boolean }) =>
      websiteService.toggleQueryRetest(selectedWebsite!.id, query, enabled),
    onSuccess: (_, { enabled }) => {
      queryClient.invalidateQueries({ queryKey: ['website-detail', selectedWebsite?.id] });
      toast.success(enabled ? 'Query retesting resumed' : 'Query retesting paused');
    },
  });

  const testQueryMutation = useMutation({
    mutationFn: (query: string) =>
      queriesService.testQueries({
        queries: [query],
        targetDomain: selectedWebsite!.domain,
        resourceId: selectedWebsite!.id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queries'] });
      toast.success('Query tested successfully!');
      trackDashboardReached(1);
    },
    onError: () => {
      toast.error('Failed to test query. Please try again.');
    },
  });

  const handleAddQuery = () => {
    if (!newQueryText.trim()) return;
    setShowAddQueryModal(false);
    setNewQueryText('');
    toast('Query testing started. This may take a minute...');
    testQueryMutation.mutate(newQueryText.trim());
  };

  const { data: queries = [], isLoading } = useQuery({
    queryKey: ['queries', filterMentions, selectedWebsite?.id],
    queryFn: () => queriesService.getQueryResults(50, 0, selectedWebsite?.id),
    staleTime: 2 * 60 * 1000,
  });

  const filteredQueries = queries.filter(q => {
    const matchesSearch = q.query.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterMentions === 'all' ||
      (filterMentions === 'mentioned' && q.llmResults.some(r => r.mentioned)) ||
      (filterMentions === 'not-mentioned' && !q.llmResults.some(r => r.mentioned));
    return matchesSearch && matchesFilter;
  });

  // For free users: multiply the first query 10 times as placeholders
  const displayQueries = isSubscribed
    ? filteredQueries
    : filteredQueries.length > 0
    ? Array.from({ length: 10 }, (_, i) => ({
        ...filteredQueries[0],
        id: i === 0 ? filteredQueries[0].id : `placeholder-${i}`,
      }))
    : [];

  const handleQueryClick = (query: QueryTestResult) => {
    setSelectedQuery(query);
    setShowDetailModal(true);
  };

  const getQueryLLMs = (query: QueryTestResult) => {
    const mentioned = query.llmResults.filter(r => r.mentioned).map(r => r.llm);
    const notMentioned = ALL_LLM_PROVIDERS.filter(llm => !mentioned.includes(llm));
    return { mentioned, notMentioned };
  };

  const excludedQueries = websiteDetail?.retestExcludedQueries || [];
  const retestEnabled = websiteDetail?.retestEnabled ?? selectedWebsite?.retestEnabled ?? true;
  const excludedCount = excludedQueries.length;

  const renderQueryRow = (query: QueryTestResult, isFirst: boolean, showDivider: boolean) => {
    const { mentioned, notMentioned } = getQueryLLMs(query);
    const hasMention = mentioned.length > 0;
    const isExcluded = excludedQueries.includes(query.query);
    const isPlaceholder = query.id.startsWith('placeholder-');

    return (
      <div key={query.id} className="flex flex-col gap-4 items-start w-full">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-3 md:gap-4">
          {/* Per-query retest toggle */}
          {isSubscribed && !isPlaceholder && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleQueryRetestMutation.mutate({ query: query.query, enabled: isExcluded });
                  }}
                  disabled={toggleQueryRetestMutation.isPending}
                  className="shrink-0 p-1 rounded hover:opacity-60 transition-opacity disabled:opacity-50"
                >
                  {isExcluded ? (
                    <PauseCircle className="w-4 h-4 text-foreground opacity-30" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5 text-foreground opacity-50" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="left">
                {isExcluded
                  ? 'Retesting paused for this query. Click to resume.'
                  : 'Retested daily. Click to pause.'}
              </TooltipContent>
            </Tooltip>
          )}

          <button
            onClick={() => (isSubscribed || isFirst) && handleQueryClick(query)}
            disabled={!isSubscribed && !isFirst}
            className={`flex-1 flex flex-col md:flex-row items-start md:items-center justify-between w-full text-left transition-opacity gap-3 md:gap-4 ${
              !isSubscribed && !isFirst
                ? 'cursor-not-allowed opacity-40 select-none blur-[2px]'
                : 'hover:opacity-70 cursor-pointer'
            }`}
          >
            <div className="flex-1 flex flex-col gap-1 items-start justify-center leading-[1.5] text-foreground text-base">
              <div className="flex items-center gap-2">
                <p className="font-sans font-[450]" style={{ fontVariationSettings: "'wdth' 100" }}>
                  {query.query}
                </p>
                {hasMention ? (
                  <ThumbsUp className="w-4 h-4 text-[#00a944]" strokeWidth={3} />
                ) : (
                  <ThumbsDown className="w-4 h-4 text-[#e14944]" strokeWidth={3} />
                )}
                {isExcluded && isSubscribed && (
                  <span className="font-mono font-medium text-[10px] tracking-[0.6px] uppercase text-foreground opacity-30">
                    paused
                  </span>
                )}
              </div>
              <div className="opacity-50 overflow-hidden w-full line-clamp-2">
                <MarkdownRenderer content={query.response} className="text-sm [&>*]:mb-0 [&>*:last-child]:mb-0" />
              </div>
              <p className="font-sans text-xs opacity-50">
                Tested {formatTimeAgo(query.timestamp)}
              </p>
            </div>
            <div className="flex gap-2 items-start w-full md:w-[320px] shrink-0">
              <div className="flex-1 flex flex-col gap-2 items-end justify-center">
                <div className="flex gap-2 items-center justify-end w-full">
                  <ThumbsUp className="w-3 h-3 text-[#00a944]" strokeWidth={3} />
                  <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#00a944]">Mention</span>
                </div>
                <div className="flex h-6 items-center justify-end w-full">
                  {mentioned.map((llm) => (
                    <div key={llm} className="bg-card border border-border rounded-full overflow-hidden size-6">
                      <div className="flex items-center justify-center size-full p-1">
                        <img
                          alt=""
                          className="max-w-none object-cover pointer-events-none size-full"
                          src={LLM_PROVIDER_LOGOS[llm]}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-2 items-start">
                <div className="flex gap-2 items-center justify-end w-full">
                  <ThumbsDown className="w-3 h-3 text-[#e14944]" strokeWidth={3} />
                  <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-[#e14944]">No Mention</span>
                </div>
                <div className="flex h-6 items-center justify-end pr-1.5 w-full">
                  {notMentioned.map((llm, idx) => (
                    <div
                      key={llm}
                      className="bg-card border border-border rounded-full overflow-hidden size-6 -mr-1.5"
                      style={{ zIndex: notMentioned.length - idx }}
                    >
                      <div className="flex items-center justify-center size-full p-0.5">
                        <img
                          alt=""
                          className="max-w-none object-cover pointer-events-none size-full"
                          src={LLM_PROVIDER_LOGOS[llm]}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </button>
        </div>
        {showDivider && <div className="bg-border h-px w-full" />}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-5 items-center w-full py-5 px-4 md:px-0">
        <div className="flex flex-col gap-4 items-start w-full max-w-[1080px]">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-4 md:gap-0">
            <h1 className="font-sans font-[450] leading-[1.5] text-foreground text-[20px]" style={{ fontVariationSettings: "'wdth' 100" }}>
              All Queries
            </h1>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
              <div className="bg-card border border-border flex gap-3 h-12 items-center px-3 py-2.5 rounded w-full sm:w-[240px] md:w-[320px]">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search queries..."
                  className="flex-1 font-sans font-[450] leading-[20px] opacity-50 text-foreground text-base bg-transparent border-none outline-none"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                />
                <Search className="w-3.5 h-3.5 text-foreground opacity-50" />
              </div>

              <div className="relative">
                <select
                  value={filterMentions}
                  onChange={(e) => setFilterMentions(e.target.value as any)}
                  className="appearance-none bg-card border border-border h-12 pl-3 pr-9 rounded font-sans font-[450] text-base text-foreground cursor-pointer hover:opacity-80 transition-opacity w-full sm:w-auto"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  <option value="all">All Results</option>
                  <option value="mentioned">Mentioned</option>
                  <option value="not-mentioned">Not Mentioned</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground pointer-events-none" />
              </div>

              {isSubscribed && selectedWebsite && (
                <button
                  onClick={() => setShowAddQueryModal(true)}
                  className="bg-primary text-primary-foreground h-12 px-4 rounded font-sans font-[450] text-base flex items-center gap-2 hover:opacity-80 transition-opacity"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add Query</span>
                </button>
              )}
            </div>
          </div>

          {/* Retest banner */}
          {isSubscribed && selectedWebsite && (
            <div className="bg-card border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-5 rounded w-full">
              <div className="flex items-center gap-3">
                <RefreshCw className={`w-4 h-4 shrink-0 ${retestEnabled ? 'text-foreground' : 'text-foreground opacity-30'}`} />
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <p className="font-mono font-medium leading-[1.5] text-foreground text-xs tracking-[0.6px] uppercase">
                      Daily Retesting
                    </p>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-3 w-3 text-foreground opacity-50 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[260px]">
                        When enabled, all your queries are automatically retested every 24 hours to track changes in LLM visibility over time.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="font-sans text-xs text-foreground opacity-50">
                    {selectedWebsite.lastRetestedAt
                      ? `Last retested ${formatTimeAgo(selectedWebsite.lastRetestedAt)}`
                      : 'Not retested yet'}
                    {excludedCount > 0 && retestEnabled && (
                      <> · {excludedCount} {excludedCount === 1 ? 'query' : 'queries'} paused</>
                    )}
                  </p>
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Switch
                      checked={retestEnabled}
                      onCheckedChange={(checked) => retestSettingsMutation.mutate(checked)}
                      disabled={retestSettingsMutation.isPending}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {retestEnabled ? 'Turn off daily retesting for this website' : 'Turn on daily retesting for this website'}
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center w-full py-20">
              <Loader2 className="h-8 w-8 animate-spin text-foreground" />
            </div>
          ) : (
            <div className="relative bg-card border border-border flex flex-col gap-5 items-start p-6 rounded w-full">
              <div className="flex flex-col items-start w-full">
                <p className="font-mono font-medium leading-[1.5] opacity-50 text-foreground text-xs tracking-[0.6px] uppercase">
                  Queries (Showing {isSubscribed ? displayQueries.length : queries.length || displayQueries.length})
                </p>
              </div>

              {displayQueries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 w-full">
                  <p className="font-sans text-base text-foreground opacity-50">
                    No queries found
                  </p>
                </div>
              ) : (
                <>
                  {/* First query - always visible */}
                  {displayQueries.length > 0 && renderQueryRow(displayQueries[0], true, displayQueries.length > 1)}

                  {/* Remaining queries - paywalled for non-subscribers */}
                  {displayQueries.length > 1 && (
                    <div className="relative flex flex-col gap-4 items-start w-full">
                      {displayQueries.slice(1).map((query, index) =>
                        renderQueryRow(query, false, index < displayQueries.length - 2)
                      )}
                      <ResultsPaywallOverlay show={!isSubscribed} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {selectedQuery && (
          <QueryDetailModal
            open={showDetailModal}
            onOpenChange={setShowDetailModal}
            query={selectedQuery.query}
            responses={ALL_LLM_PROVIDERS.map(llm => {
              const existingResult = selectedQuery.llmResults.find(r => r.llm === llm);
              const timestamp = new Date(selectedQuery.timestamp).toLocaleString('en-GB', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }).replace(',', ' ');

              return {
                llm,
                llmIcon: LLM_PROVIDER_LOGOS[llm],
                response: existingResult?.responseText || '',
                mentioned: existingResult?.mentioned ?? false,
                timestamp,
              };
            })}
          />
        )}

        <Dialog.Root open={showAddQueryModal} onOpenChange={setShowAddQueryModal}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
            <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg p-6 w-full max-w-[500px] z-50">
              <Dialog.Close className="absolute top-4 right-4 text-foreground opacity-50 hover:opacity-100">
                <X className="w-5 h-5" />
              </Dialog.Close>

              <Dialog.Title className="font-sans font-[450] text-xl text-foreground mb-4" style={{ fontVariationSettings: "'wdth' 100" }}>
                Add New Query
              </Dialog.Title>

              <Dialog.Description className="font-sans text-sm text-foreground opacity-70 mb-4">
                Enter a query to test across all LLM providers. Results will be added to your query history.
              </Dialog.Description>

              <textarea
                value={newQueryText}
                onChange={(e) => setNewQueryText(e.target.value)}
                placeholder="e.g., What are the best tools for..."
                className="w-full bg-background border border-border rounded p-3 font-sans text-base text-foreground placeholder:opacity-50 resize-none h-24 focus:outline-none focus:border-primary"
              />

              <div className="flex justify-end gap-3 mt-4">
                <Dialog.Close asChild>
                  <button
                    className="bg-card border border-border h-10 px-4 rounded font-sans font-[450] text-base text-foreground hover:opacity-80 transition-opacity"
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  onClick={handleAddQuery}
                  disabled={!newQueryText.trim()}
                  className="bg-primary text-primary-foreground h-10 px-4 rounded font-sans font-[450] text-base hover:opacity-80 transition-opacity disabled:opacity-50"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  Test Query
                </button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </TooltipProvider>
  );
};

export default DashboardQueriesPage;
